import React, { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { ModalSize } from 'app/shared/componentsRestyled/coreDesignSystem/Modals';
import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_DATETIME_FORMAT } from 'constants/date';

import { selectUserData } from 'store/auth/selectors';
import { openDialogAction } from 'store/global/actions';
import { selectApplicationContactsWithBorrowers } from 'store/loanApplications/selectors';

import EmailCreationModal from '../../EmailCreationModal';
import {
  MAX_SUBJECT_LENGTH,
  TRUNCATED_SUBJECT_LENGTH,
} from '../../EmailCreationModal/EmailCreationModal.config';
import useActivitiesContext from '../useActivitiesContext';

import { findContacts } from './utils';

const useNewEmailModal = activity => {
  const dispatch = useDispatch();
  const {
    id,
    from,
    to,
    cc,
    bcc,
    subject,
    body,
    activityDateTime,
    attachmentNames,
    linkedResourceDescriptions,
  } = activity || {};

  const { applicationId } = useParams();

  const contactsWithBorrowers = useSelector(selectApplicationContactsWithBorrowers);
  const senderData = useSelector(selectUserData);

  const { triggerScrollIntoView } = useActivitiesContext();

  const dialogRef = useRef(null);

  const openCreateEmailModal = useCallback(
    (defaultValues, repliedMessage, isForward = false) =>
      () => {
        const wrappedRepliedMessage = repliedMessage
          ? `
            <br/>
            <div style="border-left: 3px solid lightgray; padding-left: 2rem">
              ${repliedMessage}
            </div>
          `
          : '';

        const forwardedAttachmentNames = isForward ? attachmentNames : [];
        const forwardedLinkedResources =
          isForward || !!repliedMessage ? linkedResourceDescriptions : [];

        const header = defaultValues?.subject
          ? _.truncate(defaultValues?.subject, { length: TRUNCATED_SUBJECT_LENGTH })
          : 'New Email';

        dispatch(
          openDialogAction({
            header,
            size: ModalSize.XL,
            disableEnforceFocus: true,
            ref: dialogRef,
            body: dialogProps => (
              <EmailCreationModal
                {...dialogProps}
                applicationId={applicationId}
                defaultValues={defaultValues}
                dialogRef={dialogRef}
                forwardedAttachmentNames={forwardedAttachmentNames}
                forwardedLinkedResources={forwardedLinkedResources}
                inReplyTo={id}
                isForward={isForward}
                repliedMessage={wrappedRepliedMessage}
                triggerScrollIntoView={triggerScrollIntoView}
              />
            ),
          }),
        );
      },
    [
      dispatch,
      applicationId,
      id,
      attachmentNames,
      linkedResourceDescriptions,
      triggerScrollIntoView,
    ],
  );

  const onCreateEmail = useMemo(() => openCreateEmailModal(), [openCreateEmailModal]);

  const onContactBorrower = useCallback(
    emailAddress => {
      const contact = contactsWithBorrowers.find(
        ({ value }) => _.toLower(value) === _.toLower(emailAddress),
      );

      return openCreateEmailModal({
        recipients: contact ? [contact] : [],
      })();
    },
    [contactsWithBorrowers, openCreateEmailModal],
  );

  const onCreateReply = useMemo(() => {
    const recipientContact =
      from !== senderData.email &&
      contactsWithBorrowers.find(({ value }) => value.toLowerCase() === from);

    return openCreateEmailModal(
      {
        recipients: recipientContact ? [recipientContact] : [],
        subject: `Re: ${subject}`.slice(0, MAX_SUBJECT_LENGTH),
      },
      body,
    );
  }, [body, contactsWithBorrowers, from, openCreateEmailModal, senderData.email, subject]);

  const onCreateReplyAll = useMemo(() => {
    const recipients = findContacts(contactsWithBorrowers, to, senderData.email);
    const ccContacts = findContacts(contactsWithBorrowers, cc, senderData.email);
    const fromContact = findContacts(contactsWithBorrowers, [from], senderData.email);

    return openCreateEmailModal(
      {
        recipients: recipients.concat(fromContact),
        cc: ccContacts,
        subject: `Re: ${subject}`.slice(0, MAX_SUBJECT_LENGTH),
      },
      body,
    );
  }, [body, contactsWithBorrowers, openCreateEmailModal, senderData, subject, cc, to, from]);

  const onCreateForward = useMemo(() => {
    const emailDate = dayjs(activityDateTime).format(COMMON_DATETIME_FORMAT);

    const repliedMessage = `
      -----Forwarded message-----<br/>
      From: ${from}<br/>
      To: ${Array.isArray(to) ? to.join(', ') : to}<br/>
      ${cc?.length ? `CC: ${cc.join(', ')}<br/>` : ''}
      ${bcc?.length ? `BCC: ${bcc.join(', ')}<br/>` : ''}
      Date: ${emailDate}<br/>
      Subject: ${subject}<br/><br/>
      ${body}
    `;

    return openCreateEmailModal(
      {
        subject: `Fwd: ${subject}`.slice(0, MAX_SUBJECT_LENGTH),
      },
      repliedMessage,
      true,
    );
  }, [activityDateTime, bcc, body, cc, from, openCreateEmailModal, subject, to]);

  return { onCreateEmail, onCreateReply, onCreateReplyAll, onCreateForward, onContactBorrower };
};

export default useNewEmailModal;
